import React, { Component } from 'react';
import {
  CWidgetDropdown,
  CRow,
  CCol,
} from '@coreui/react'
import ChartLineSimple from '../charts/ChartLineSimple'
import ChartBarSimple from '../charts/ChartBarSimple'
import {buildConnection} from 'mui-metablockchain-sdk/src/connection'
import Elapsed from './Elapsed'
import {netstat} from '../../config'

// Constants
const API_CONFIG = {
  BASE_URL: 'https://api.routescan.io/v2/network',
  NETWORK_ID: 'mainnet', // or 'testnet'
  CHAIN_ID: '43114', // or '43113' for testnet
  CONTRACT_ADDRESS: '0xFc8A21dBCAB432FB5E469d80F976E022C2f56Ea0',
  DECIMALS: 6
};


const WIDGET_STYLES = {
  maxWidth: '100%'
};

const CHART_STYLES = {
  height: '70px'
};

class WidgetsDropdown extends Component {

  state = {
    latestBlockNumber: '0',
    confirmedBlockCount: '0',
    blockTimestamp: '0',
    metamuiTotalIssuance: '0',
    mmuicTotalIssuance: '0',
    connectionStatus: 'connecting',
    retryAttempts: 0,
    lastError: null,
  }

  // Flag to track component mount status
  _isMounted = false;

  constructor(props) {
    super(props);
    this.blockchainSubscription = null;
    this.blockchainApi = null;
    this.connectionRetryTimer = null;
    this.heartbeatTimer = null;
    this.lastErrorTime = 0; // Track last error to prevent spam
    this.visibilityChangeHandler = null;
    this.wasPageHidden = false;
  }

  formatTotalIssuance = (totalSupplyValue, currencyCode) => {
    const numericValue = parseFloat(totalSupplyValue);
    
    const TRILLION = 1e12;
    const BILLION = 1e9;
    const MILLION = 1e6;
    
    if (numericValue >= TRILLION) {
      let value = (numericValue / TRILLION).toFixed(1).toLocaleString();
      return `${value} Trillion ${currencyCode}`;
    } else if (numericValue >= BILLION) {
      let value = (numericValue / BILLION).toFixed(1).toLocaleString();
      return `${value} Billion ${currencyCode}`;
    } else if (numericValue >= MILLION) {
      let value = (numericValue / MILLION).toFixed(1).toLocaleString();
      return `${value} Million ${currencyCode}`;
    } else {
      return `${numericValue.toLocaleString()} ${currencyCode}`;
    }
  }

  fetchMmuicTotalIssuance = async () => {
    try {
      const { BASE_URL, NETWORK_ID, CHAIN_ID, CONTRACT_ADDRESS, DECIMALS } = API_CONFIG;
      const apiEndpointUrl = `${BASE_URL}/${NETWORK_ID}/evm/${CHAIN_ID}/etherscan/stats/tokensupply?contractAddress=${CONTRACT_ADDRESS}`;
      
      const apiResponse = await fetch(apiEndpointUrl);
      const responseData = await apiResponse.json();
      
      // Check if component is still mounted before updating state
      if (!this._isMounted) {
        return;
      }
      
      if (responseData.status === "1" && responseData.result) {
        const rawTotalSupply = parseFloat(responseData.result);
        const adjustedTotalSupply = rawTotalSupply / Math.pow(10, DECIMALS);
        const formattedTotalSupply = this.formatTotalIssuance(adjustedTotalSupply, 'MMUI-C');
        this.setState({ mmuicTotalIssuance: formattedTotalSupply });
      } else {
        this.setState({ mmuicTotalIssuance: 'Error fetching data' });
      }
    } catch (networkError) {
      if (this._isMounted) {
        this.setState({ mmuicTotalIssuance: 'Connection Error' });
      }
    }
  }

  // Set up page visibility change handler
  setupVisibilityChangeHandler = () => {
    this.visibilityChangeHandler = () => {
      if (document.hidden) {
        // Page is now hidden
        this.wasPageHidden = true;
        this.stopHeartbeat();
      } else {
        // Page is now visible
        if (this.wasPageHidden) {
          this.wasPageHidden = false;
          // Reconnect after page becomes visible again
          this.handlePageVisibilityReconnect();
          this.startHeartbeat();
        }
      }
    };
    
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  // Handle reconnection when page becomes visible
  handlePageVisibilityReconnect = async () => {
    if (!this._isMounted) return;
    
    // Check if connection is still active
    if (this.state.connectionStatus === 'connected' && this.blockchainApi) {
      try {
        // Test connection with a simple query
        await this.blockchainApi.rpc.system.chain();
      } catch (error) {
        // Connection is stale, reconnect
        this.forceReconnect();
      }
    } else if (this.state.connectionStatus !== 'connecting') {
      // Not connected, attempt reconnection
      this.forceReconnect();
    }
  }

  // Start heartbeat to periodically check connection
  startHeartbeat = () => {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }
    
    this.heartbeatTimer = setInterval(() => {
      if (!this._isMounted || document.hidden) return;
      
      // Only check if we think we're connected
      if (this.state.connectionStatus === 'connected' && this.blockchainApi) {
        this.checkConnectionHealth();
      }
    }, 30000); // Check every 30 seconds
  }

  // Stop heartbeat
  stopHeartbeat = () => {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  // Check if connection is still healthy
  checkConnectionHealth = async () => {
    try {
      // Simple query to test connection
      await Promise.race([
        this.blockchainApi.rpc.system.chain(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Heartbeat timeout')), 5000))
      ]);
    } catch (error) {
      // Connection is unhealthy, reconnect
      if (this._isMounted) {
        this.setState({ connectionStatus: 'reconnecting' });
        this.forceReconnect();
      }
    }
  }

  // Manual reconnection method for debugging
  forceReconnect = async () => {
    this.cleanupConnection();
    await this.establishBlockchainConnection();
  }

  componentDidMount() {
    // Expose reconnect method globally for debugging
    window.debugReconnectBlockchain = this.forceReconnect;
    
    this._isMounted = true;
    
    // Set up page visibility change handler
    this.setupVisibilityChangeHandler();
    
    // Start connection attempt
    this.establishBlockchainConnection();
    
    // Fetch MMUI-C total issuance data (independent of blockchain connection)
    this.fetchMmuicTotalIssuance();
    
    // Start heartbeat to detect stale connections
    this.startHeartbeat();
  }

  establishBlockchainConnection = async () => {
    try {
      this.setState({ 
        connectionStatus: 'connecting',
        retryAttempts: 1,
        lastError: null
      });

      // Create fresh blockchain connection to avoid stale connections
      const connectionPromise = buildConnection(netstat, true); // Force fresh connection
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Connection timeout after 10s')), 10000);
      });

      this.blockchainApi = await Promise.race([connectionPromise, timeoutPromise]);
      
      if (!this._isMounted) {
        return;
      }

      // Check if API was created successfully
      if (!this.blockchainApi) {
        throw new Error('buildConnection returned null/undefined');
      }

      // Wait for API to be ready  
      await this.blockchainApi.isReady;
      
      // Give some stabilization time
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (!this._isMounted) {
        return;
      }

      // Set up event handlers
      this.blockchainApi.on('disconnected', () => {
        if (this._isMounted) {
          this.setState({ 
            connectionStatus: 'failed',
            latestBlockNumber: '--',
            confirmedBlockCount: '--',
            metamuiTotalIssuance: '--'
          });
        }
      });

      // Try one simple operation to test if it really works
      try {
        await this.initializeMetaMUIData(this.blockchainApi);
        
        this.setState({ connectionStatus: 'connected' });
        
        // Subscribe to updates
        await this.subscribeToBlockchainUpdates(this.blockchainApi);
        
        return; // Success!
        
      } catch (testError) {
        throw testError;
      }
      
    } catch (error) {
      // Clean up any partial connections
      this.cleanupConnection();
      
      // Set offline mode immediately - no retries to prevent spam
      if (this._isMounted) {
        this.setState({
          latestBlockNumber: '--',
          confirmedBlockCount: '--', 
          metamuiTotalIssuance: 'Offline Mode',
          connectionStatus: 'offline',
          lastError: 'Blockchain temporarily unavailable'
        });
      }
    }
  }

  cleanupConnection = () => {
    // Clean up subscription
    if (this.blockchainSubscription) {
      try {
        if (typeof this.blockchainSubscription.unsubscribe === 'function') {
          this.blockchainSubscription.unsubscribe();
        } else if (typeof this.blockchainSubscription === 'function') {
          this.blockchainSubscription();
        }
        this.blockchainSubscription = null;
      } catch (error) {
        // Ignore cleanup errors
      }
    }

    // Clean up API connection
    if (this.blockchainApi) {
      try {
        // Remove event listeners
        if (typeof this.blockchainApi.off === 'function') {
          this.blockchainApi.off('disconnected');
          this.blockchainApi.off('error');
        }
        
        // Disconnect the API
        if (typeof this.blockchainApi.disconnect === 'function') {
          this.blockchainApi.disconnect();
        }
        this.blockchainApi = null;
      } catch (error) {
        // Ignore cleanup errors
      }
    }
  }

  initializeMetaMUIData = async (blockchainApi) => {
    try {
      // Add timeout to the query
      const queryPromise = blockchainApi.query.balances.totalIssuance();
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Query timeout after 8s')), 8000);
      });

      const totalIssuanceQuery = await Promise.race([queryPromise, timeoutPromise]);

      const { DECIMALS } = API_CONFIG;
      const adjustedTotalSupply = Number(totalIssuanceQuery.toString()) / Math.pow(10, DECIMALS);
      const formattedTotalSupply = this.formatTotalIssuance(adjustedTotalSupply, 'MMUI');
      console.log('Formatted Total Issuance:', formattedTotalSupply);
      
      // Only update state if component is still mounted
      if (this._isMounted) {
        this.setState({ metamuiTotalIssuance: formattedTotalSupply });
      }
    } catch (initializationError) {
      if (this._isMounted) {
        this.setState({ metamuiTotalIssuance: 'Connection Error' });
      }
      throw initializationError; // Re-throw to allow retry logic
    }
  }

  subscribeToBlockchainUpdates = async (blockchainApi) => {
    try {
      // Check if API exists
      if (!blockchainApi) {
        throw new Error('API is null in subscription');
      }

      // Store the unsubscribe function returned by subscribeNewHeads
      this.blockchainSubscription = await blockchainApi.rpc.chain.subscribeNewHeads(async (latestBlockHeader) => {
        // Check if component is still mounted before processing updates
        if (!this._isMounted) {
          return;
        }

        try {
          const finalizedBlockNumber = await blockchainApi.derive.chain.bestNumberFinalized();
          const currentBlockTimestamp = await blockchainApi.query.timestamp.now();
          
          // Double-check mount status after async operations
          if (this._isMounted) {
            this.setState({
              latestBlockNumber: `${latestBlockHeader.number}`,
              confirmedBlockCount: finalizedBlockNumber.words[0],
              blockTimestamp: currentBlockTimestamp
            });
          }
        } catch (updateError) {
          // If we get connection errors, trigger reconnection
          if (updateError.message.includes('WebSocket is not connected')) {
            this.setState({ connectionStatus: 'reconnecting' });
            this.cleanupConnection();
            setTimeout(() => this.establishBlockchainConnection(), 2000);
          }
        }
      });

    } catch (subscriptionError) {
      // Handle specific WebSocket connection errors
      if (subscriptionError.message.includes('WebSocket is not connected')) {
        if (this._isMounted) {
          this.setState({ 
            connectionStatus: 'failed',
            lastError: 'WebSocket connection failed'
          });
        }
      }
      throw subscriptionError; // Re-throw to trigger retry
    }
  }

  componentWillUnmount() {
    // Set mounted flag to false to prevent state updates and callback execution
    this._isMounted = false;
    
    // Clean up visibility change listener
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }
    
    // Stop heartbeat
    this.stopHeartbeat();
    
    // Clean up blockchain connection
    this.cleanupConnection();
  }

  renderWidget = (widgetColor, headerValue, displayText, ChartComponent, chartConfiguration = {}) => (
    <CWidgetDropdown
      color={widgetColor}
      header={headerValue}
      text={displayText}
      className="text-truncate"
      style={WIDGET_STYLES}
      footerSlot={
        <ChartComponent
          className="mt-3 mx-3"
          style={CHART_STYLES}
          {...chartConfiguration}
        />
      }
    />
  )
  

  render() {
    return (
      <>
        <CRow>
          <CCol sm="6" md="4" lg>
            <Elapsed value={this.state.blockTimestamp} style={{ height: '158px' }} />
          </CCol>

          <CCol sm="6" md="4" lg>
            {this.renderWidget(
              'gradient-primary',
              this.state.latestBlockNumber.toString(),
              'Latest block',
              ChartLineSimple,
              {
                pointed: true,
                className: 'c-chart-wrapper mt-3 mx-3',
                dataPoints: [65, 59, 84, 84, 51, 55, 40],
                pointHoverBackgroundColor: 'primary',
                label: 'BlockTime'
              }
            )}
          </CCol>

          <CCol sm="6" md="4" lg>
            {this.renderWidget(
              'gradient-warning',
              this.state.confirmedBlockCount.toString(),
              'Confirmed Block',
              ChartLineSimple,
              {
                backgroundColor: 'rgba(255,255,255,.2)',
                dataPoints: [78, 81, 80, 45, 34, 12, 40],
                options: { elements: { line: { borderWidth: 2.5 } } },
                pointHoverBackgroundColor: 'warning',
                label: 'Module',
                labels: 'months'
              }
            )}
          </CCol>

          <CCol sm="6" md="4" lg>
            {this.renderWidget(
              'gradient-danger',
              this.state.metamuiTotalIssuance,
              'MetaMUI Total Issuance',
              ChartBarSimple,
              {
                backgroundColor: 'rgb(250, 152, 152)',
                label: 'Runtime',
                labels: 'months'
              }
            )}
          </CCol>

          <CCol sm="6" md="4" lg>
            {this.renderWidget(
              'gradient-success',
              this.state.mmuicTotalIssuance,
              'MMUI-C Total Issuance',
              ChartBarSimple,
              {
                backgroundColor: 'rgb(152, 250, 152)',
                label: 'MMUI-C',
                labels: 'time'
              }
            )}
          </CCol>
        </CRow>
    </>
  )
}
}

export default WidgetsDropdown
